import React, { useState, useEffect, useCallback, useRef, Component } from 'react';
import { Toaster, toast } from 'sonner';
import WizardProgress from './components/WizardProgress';
import ThemeToggle from './components/ThemeToggle';
import SolutgenSupportWidget from './components/SolutgenSupportWidget';
import { Button } from './components/ui/button';
import { Input } from './components/ui/input';
import { Label } from './components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from './components/ui/dialog';
import StepUserData from './steps/StepUserData';
import StepQuestions from './steps/StepQuestions';
import StepWheel from './steps/StepWheel';
import StepLifeSquares from './steps/StepLifeSquares';
import StepObjectives from './steps/StepObjectives';
import StepSummary from './steps/StepSummary';
import StepAffirmationPhrases from './steps/StepAffirmationPhrases';
import StepAudioOptional from './steps/StepAudioOptional';
import AudioPage from './pages/AudioPage';
import AdminApp from './admin/AdminApp';
import { coachingQuestions, generateLowScoreQuestions } from './lib/questions';
import {
  clearLocalStorage,
  saveToLocalStorage,
  loadFromLocalStorage,
  getSessionId,
  setSessionId as persistSessionId,
} from './lib/storage';
import { getFormJourneyConfig } from './lib/formJourney';
import { consumePortalFormHandoff, createSubmission, getSubmissionBySession, updateSubmission, finalizeSubmission, saveSubmissionForLater } from './lib/api';
import {
  getPortalJourneyRequest,
  removePortalHandoffToken,
  validatePortalHandoff,
} from './lib/portalHandoff';
import { Loader2, AlertCircle, Save } from 'lucide-react';
import './App.css';

// Error Boundary to catch React errors
class ErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null, errorInfo: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true };
  }

  componentDidCatch(error, errorInfo) {
    this.setState({ error, errorInfo });
    console.error('Error Boundary caught:', error, errorInfo);
  }

  render() {
    if (this.state.hasError) {
      return (
        <div className="min-h-screen flex items-center justify-center bg-background p-4">
          <div className="max-w-md text-center space-y-4">
            <AlertCircle className="w-16 h-16 text-red-500 mx-auto" />
            <h1 className="text-2xl font-bold text-red-600">Algo salió mal</h1>
            <p className="text-muted-foreground">
              Ocurrió un error inesperado. Tu progreso está guardado.
            </p>
            <p className="text-xs text-red-500 bg-red-50 p-2 rounded overflow-auto max-h-32">
              {this.state.error?.toString()}
            </p>
            <button 
              onClick={() => window.location.reload()} 
              className="px-4 py-2 bg-primary text-white rounded hover:bg-primary/90"
            >
              Recargar página
            </button>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}

// Check if we're on the admin route
const isAdminRoute = () => {
  return window.location.pathname.startsWith('/admin');
};

const isAudioRoute = () => {
  return window.location.pathname.startsWith('/audio');
};

const getFormVariantFromPath = () => (
  window.location.pathname.startsWith('/classic') ? 'classic' : 'formu2'
);

const PORTAL_JOURNEY_ENABLED =
  process.env.REACT_APP_PORTAL_JOURNEY_ENABLED === 'true';
const PORTAL_URL =
  process.env.REACT_APP_PORTAL_URL || 'https://portal.academiacortex.com.ar/inicio';

const classicSteps = [
  { id: 0, label: 'Datos' },
  { id: 1, label: 'Preguntas' },
  { id: 2, label: 'Cuadraditos' },
  { id: 3, label: 'Contexto' },
  { id: 4, label: 'Rueda' },
  { id: 5, label: 'Reflexión' },
  { id: 6, label: 'Objetivos' },
  { id: 7, label: 'Cierre' },
  { id: 8, label: 'Resumen' },
];

const formu2Steps = [
  ...classicSteps.slice(0, 8),
  { id: 8, label: 'Frases' },
  { id: 9, label: 'Audio' },
  { id: 10, label: 'Resumen' },
];

const DEFAULT_OBJECTIVES = {
  '3m': [{ description: '', dailyTime: '00:30', actions: ['', '', ''] }],
  '6m': [{ description: '', dailyTime: '00:30', actions: ['', '', ''] }],
  '9m': [{ description: '', dailyTime: '00:30', actions: ['', '', ''] }],
};

const getQuestionMetadata = (questionId, wheelData) => {
  for (const section of Object.values(coachingQuestions)) {
    const question = (section.questions || []).find((item) => item.id === questionId);
    if (question) {
      return {
        question_text: question.text || '',
        section: section.title || '',
      };
    }
  }

  const dynamicQuestion = generateLowScoreQuestions(wheelData).find((item) => item.id === questionId);
  if (dynamicQuestion) {
    return {
      question_text: dynamicQuestion.text || '',
      section: coachingQuestions.sectionJ.title,
    };
  }

  return {
    question_text: '',
    section: '',
  };
};

function FormApp({
  formVariant = 'classic',
  portalJourneyRequest = { portalJourney: false, token: '' },
}) {
  const isFormu2 = formVariant === 'formu2';
  const portalJourneyRequested =
    isFormu2 && portalJourneyRequest.portalJourney;
  const {
    finalizeStep,
    isPortalJourney,
    offerSaveForLaterPassword,
    summaryStep,
    useLegacyAudioSteps,
  } = getFormJourneyConfig({
    isFormu2,
    portalJourneyEnabled: PORTAL_JOURNEY_ENABLED || portalJourneyRequested,
  });
  const storageScope = isFormu2 ? 'formu2' : 'classic';
  const steps = isPortalJourney ? classicSteps : isFormu2 ? formu2Steps : classicSteps;
  const [currentStep, setCurrentStep] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [sessionId, setSessionId] = useState(() => getSessionId(storageScope));
  const initialSessionIdRef = useRef(sessionId);
  const loadStartedRef = useRef(false);
  const [submissionId, setSubmissionId] = useState(null);
  const [isSubmissionLocked, setIsSubmissionLocked] = useState(false);
  const [portalIdentity, setPortalIdentity] = useState(null);
  const [portalHandoffError, setPortalHandoffError] = useState('');

  // Form data state
  const [userData, setUserData] = useState({ name: '', email: '' });
  const [capacitacionId, setCapacitacionId] = useState(null);
  const [wheelData, setWheelData] = useState({});
  const [lifeSquares, setLifeSquares] = useState({ currentAge: '', targetAge: 90 });
  const [objectives, setObjectives] = useState(DEFAULT_OBJECTIVES);
  const [questionAnswers, setQuestionAnswers] = useState({});
  const [affirmationPhrases, setAffirmationPhrases] = useState(Array(6).fill(''));
  const [audioToken, setAudioToken] = useState('');
  const [audioUrl, setAudioUrl] = useState('');
  const [audioStatus, setAudioStatus] = useState('not_started');
  const [rmcPasswordConfigured, setRmcPasswordConfigured] = useState(false);
  const [saveLaterDialogOpen, setSaveLaterDialogOpen] = useState(false);
  const [saveLaterPassword, setSaveLaterPassword] = useState('');
  const [saveLaterPasswordConfirm, setSaveLaterPasswordConfirm] = useState('');
  const [isSavingForLaterAccess, setIsSavingForLaterAccess] = useState(false);
  const saveLaterResolveRef = useRef(null);
  const [resumePasswordRequired, setResumePasswordRequired] = useState(false);
  const [resumePassword, setResumePassword] = useState('');

  const applySubmissionData = useCallback((sub, options = {}) => {
    if (!sub) return;

    const {
      lockIfFinalized = false,
      savedData = null,
      preferLocalAnswers = false,
    } = options;

    setSubmissionId(sub._id);
    setUserData(sub.user || { name: '', email: '' });
    setCapacitacionId(sub.capacitacion_id || null);
    setWheelData(sub.wheel_data || {});
    setLifeSquares(sub.life_squares || { currentAge: '', targetAge: 90 });
    setObjectives(sub.objectives || DEFAULT_OBJECTIVES);
    setAudioStatus(sub.affirmation_audio?.status || 'not_started');
    setRmcPasswordConfigured(
      sub.audio_workflow?.rmc_app_sync?.password_status === 'configured'
    );
    if (sub.affirmation_audio?.phrases) {
      setAffirmationPhrases([...sub.affirmation_audio.phrases, ...Array(6).fill('')].slice(0, 6));
    }
    if (sub.question_answers) {
      const serverAnswers = {};
      sub.question_answers.forEach((qa) => {
        serverAnswers[qa.question_id] = qa.answer;
      });
      if (preferLocalAnswers) {
        const localAnswers = savedData?.questionAnswers || {};
        setQuestionAnswers((prev) => ({ ...serverAnswers, ...localAnswers, ...prev }));
      } else {
        setQuestionAnswers(serverAnswers);
      }
    } else {
      setQuestionAnswers({});
    }
    if (lockIfFinalized && sub.status === 'finalized') {
      setIsSubmissionLocked(true);
    }
  }, []);

  // Load saved data on mount
  useEffect(() => {
    if (loadStartedRef.current) return;
    loadStartedRef.current = true;

    const loadData = async () => {
      try {
        // First check localStorage
        let savedData = loadFromLocalStorage(storageScope);
        let activeSessionId = initialSessionIdRef.current;

        if (portalJourneyRequested) {
          if (portalJourneyRequest.token) {
            const rawHandoff = await consumePortalFormHandoff(
              portalJourneyRequest.token
            );
            const handoff = validatePortalHandoff(rawHandoff);
            activeSessionId = handoff.sessionId;
            const savedEmail = String(savedData?.userData?.email || '')
              .trim()
              .toLowerCase();
            if (
              initialSessionIdRef.current !== activeSessionId ||
              (savedEmail && savedEmail !== handoff.user.email)
            ) {
              clearLocalStorage(storageScope);
              savedData = null;
            }
            setSessionId(activeSessionId);
            persistSessionId(activeSessionId, storageScope);
            setPortalIdentity(handoff.user);
            setUserData(handoff.user);
            savedData = saveToLocalStorage({
              userData: handoff.user,
              portalJourney: true,
            }, storageScope);
            window.history.replaceState(
              {},
              '',
              removePortalHandoffToken(window.location)
            );
          } else if (savedData?.portalJourney && savedData?.userData) {
            setPortalIdentity(savedData.userData);
          } else {
            throw new Error('missing_portal_handoff');
          }
        }

        if (savedData) {
          if (savedData.userData && !portalJourneyRequest.token) {
            setUserData(savedData.userData);
          }
          if (savedData.capacitacionId) setCapacitacionId(savedData.capacitacionId);
          if (savedData.wheelData) setWheelData(savedData.wheelData);
          if (savedData.lifeSquares) setLifeSquares(savedData.lifeSquares);
          if (savedData.objectives) setObjectives(savedData.objectives);
          if (savedData.questionAnswers) setQuestionAnswers(savedData.questionAnswers);
          if (savedData.affirmationPhrases) setAffirmationPhrases(savedData.affirmationPhrases);
          if (savedData.audioToken) setAudioToken(savedData.audioToken);
          if (savedData.audioUrl) setAudioUrl(savedData.audioUrl);
          if (savedData.audioStatus) setAudioStatus(savedData.audioStatus);
          if (savedData.rmcPasswordConfigured || savedData.rmcAccessConfigured) {
            setRmcPasswordConfigured(true);
          }
          if (savedData.currentStep) {
            setCurrentStep(Math.min(savedData.currentStep, summaryStep));
          }
          if (savedData.submissionId) setSubmissionId(savedData.submissionId);
        }

        // Then check server for existing submission
        const result = await getSubmissionBySession(activeSessionId);
        if (result.found && result.submission) {
          const sub = result.submission;
          applySubmissionData(sub, { savedData, preferLocalAnswers: true });
          if (sub.status === 'finalized') {
            setCurrentStep(summaryStep);
          } else if (sub.current_step !== undefined && sub.current_step !== null) {
            // Only use server step if localStorage doesn't have a more recent one
            if (!savedData?.currentStep || sub.current_step >= savedData.currentStep) {
              setCurrentStep(Math.min(sub.current_step, summaryStep));
            }
          }
        } else if (savedData?.submissionId) {
          setSubmissionId(null);
          saveToLocalStorage({ submissionId: null }, storageScope);
          if ((savedData.currentStep || 0) >= summaryStep) {
            setCurrentStep(finalizeStep);
            toast.warning('No encontramos este formulario guardado en el servidor. Revisá el cierre y finalizalo para guardarlo correctamente.');
          }
        }
      } catch (error) {
        console.error('Error loading data:', error);
        if (portalJourneyRequested) {
          setPortalHandoffError(
            'No pudimos vincular el Planificador con tu cuenta de la academia. Volvé al Portal e intentá nuevamente.'
          );
        }
      } finally {
        setIsLoading(false);
      }
    };

    loadData();
  }, [
    applySubmissionData,
    finalizeStep,
    portalJourneyRequest.token,
    portalJourneyRequested,
    storageScope,
    summaryStep,
  ]);

  // Auto-save to localStorage
  const saveData = useCallback(() => {
    saveToLocalStorage({
      userData,
      capacitacionId,
      wheelData,
      lifeSquares,
      objectives,
      questionAnswers,
      affirmationPhrases,
      audioToken,
      audioUrl,
      audioStatus,
      rmcPasswordConfigured,
      currentStep,
      submissionId,
      isSubmissionLocked,
    }, storageScope);
  }, [userData, capacitacionId, wheelData, lifeSquares, objectives, questionAnswers, affirmationPhrases, audioToken, audioUrl, audioStatus, rmcPasswordConfigured, currentStep, submissionId, isSubmissionLocked, storageScope]);

  useEffect(() => {
    if (!isLoading) {
      saveData();
    }
  }, [saveData, isLoading]);

  useEffect(() => {
    setResumePasswordRequired(false);
    setResumePassword('');
  }, [userData.email]);

  const buildSubmissionPayload = useCallback((step = currentStep) => ({
    wheel_data: wheelData,
    life_squares: lifeSquares,
    objectives: objectives,
    question_answers: Object.entries(questionAnswers).map(([id, answer]) => {
      const metadata = getQuestionMetadata(id, wheelData);
      return {
        question_id: id,
        question_text: metadata.question_text,
        answer: answer,
        section: metadata.section,
      };
    }),
    current_step: step,
  }), [wheelData, lifeSquares, objectives, questionAnswers, currentStep]);

  const createOrResumeSubmission = useCallback(async (passwordOverride = '') => {
    const passwordForResume = passwordOverride || (resumePasswordRequired ? resumePassword : '');
    const submission = await createSubmission(userData, sessionId, capacitacionId, formVariant, passwordForResume);
    setSubmissionId(submission._id);
    saveToLocalStorage({ submissionId: submission._id }, storageScope);

    if (submission.status === 'finalized') {
      applySubmissionData(submission, { lockIfFinalized: true });
      setCurrentStep(summaryStep);
    }

    setResumePasswordRequired(false);
    setResumePassword('');
    return submission;
  }, [userData, sessionId, capacitacionId, formVariant, resumePasswordRequired, resumePassword, storageScope, applySubmissionData, summaryStep]);

  // Save to server. When allowed, recover from missing/stale local submission IDs.
  const saveToServer = useCallback(async ({ allowCreate = false, step = currentStep, passwordOverride = '' } = {}) => {
    let targetSubmissionId = submissionId;

    if (!targetSubmissionId && allowCreate) {
      const submission = await createOrResumeSubmission(passwordOverride);
      targetSubmissionId = submission._id;
      if (submission.status === 'finalized') return submission;
    }

    if (!targetSubmissionId) return null;

    try {
      return await updateSubmission(
        targetSubmissionId,
        buildSubmissionPayload(step),
        sessionId
      );
    } catch (error) {
      if (allowCreate && error?.response?.status === 404) {
        setSubmissionId(null);
        saveToLocalStorage({ submissionId: null }, storageScope);
        const submission = await createOrResumeSubmission(passwordOverride);
        if (submission.status === 'finalized') return submission;
        return await updateSubmission(
          submission._id,
          buildSubmissionPayload(step),
          sessionId
        );
      }

      throw error;
    }
  }, [submissionId, currentStep, createOrResumeSubmission, buildSubmissionPayload, sessionId, storageScope]);

  // Handle step navigation
  const handleNext = async () => {
    let savedSubmission = null;
    try {
      savedSubmission = await saveToServer({ allowCreate: true, step: currentStep });
    } catch (error) {
      console.error('Error saving to server:', error);
      const detail = error?.response?.data?.detail;
      if (currentStep === 0 && error?.response?.status === 401 && (detail === 'password_required' || detail === 'password_invalid')) {
        setResumePasswordRequired(true);
        toast.error(
          detail === 'password_invalid'
            ? 'La contraseña no es correcta. Probá de nuevo.'
            : 'Ya hay un formulario guardado para ese email. Ingresá tu contraseña para continuar.'
        );
        return;
      }
      toast.error('No se pudo guardar en el servidor. Revisá tu conexión y probá de nuevo.');
      return;
    }

    if (savedSubmission?.reused_existing) {
      if (savedSubmission.status === 'finalized') {
        toast.info('Ya habías completado este formulario con este email.');
        return;
      }

      if (currentStep === 0) {
        const resumeStep = Math.max(
          Number.isFinite(Number(savedSubmission.current_step)) ? Number(savedSubmission.current_step) : 0,
          1
        );
        setCurrentStep(resumeStep);
        toast.info('Encontramos tu formulario anterior y retomamos donde lo dejaste.');
        return;
      }
    }

    if (currentStep === 0) {
      toast.success('Progreso guardado automáticamente');
    }

    if (currentStep === finalizeStep) {
      const targetSubmissionId = savedSubmission?._id || submissionId;
      if (!targetSubmissionId) {
        toast.error('No se pudo finalizar porque el formulario no está guardado en el servidor.');
        return;
      }

      try {
        await finalizeSubmission(targetSubmissionId, sessionId);
      } catch (error) {
        console.error('Error finalizing submission:', error);
        toast.error('No se pudo finalizar el formulario. Probá de nuevo.');
        return;
      }
    }

    setCurrentStep((prev) => Math.min(prev + 1, summaryStep));
  };

  const handleBack = () => {
    if (isSubmissionLocked) return;
    saveToServer();
    setCurrentStep((prev) => Math.max(prev - 1, 0));
  };

  const resolveSaveLaterRequest = useCallback((value) => {
    if (saveLaterResolveRef.current) {
      saveLaterResolveRef.current(value);
      saveLaterResolveRef.current = null;
    }
  }, []);

  const requestSaveForLater = useCallback(() => {
    if (isSubmissionLocked) return Promise.resolve(false);
    if (!userData.name?.trim() || userData.name.trim().length < 2 || !userData.email?.includes('@')) {
      toast.error('Completá nombre y email antes de guardar para continuar más tarde.');
      return Promise.resolve(false);
    }

    setSaveLaterPassword('');
    setSaveLaterPasswordConfirm('');
    setSaveLaterDialogOpen(true);
    return new Promise((resolve) => {
      saveLaterResolveRef.current = resolve;
    });
  }, [isSubmissionLocked, userData.email, userData.name]);

  const closeSaveLaterDialog = useCallback(() => {
    if (isSavingForLaterAccess) return;
    setSaveLaterDialogOpen(false);
    resolveSaveLaterRequest(false);
  }, [isSavingForLaterAccess, resolveSaveLaterRequest]);

  const handleConfirmSaveForLater = async (event) => {
    event.preventDefault();
    if (saveLaterPassword.length < 8) {
      toast.error('La contraseña debe tener al menos 8 caracteres.');
      return;
    }
    if (saveLaterPassword !== saveLaterPasswordConfirm) {
      toast.error('Las contraseñas no coinciden.');
      return;
    }

    setIsSavingForLaterAccess(true);
    try {
      const savedSubmission = await saveToServer({
        allowCreate: true,
        step: currentStep,
        passwordOverride: saveLaterPassword,
      });
      const targetSubmissionId = savedSubmission?._id || submissionId;
      if (!targetSubmissionId) {
        toast.error('No se pudo guardar porque el formulario no está creado en el servidor.');
        return;
      }

      const result = await saveSubmissionForLater(targetSubmissionId, sessionId, {
        current_step: currentStep,
        password: saveLaterPassword,
      });

      if (result.submission) {
        applySubmissionData(result.submission);
        const savedStep = Number.isFinite(Number(result.submission.current_step))
          ? Number(result.submission.current_step)
          : currentStep;
        setCurrentStep(Math.min(savedStep, summaryStep));
        saveToLocalStorage({
          submissionId: result.submission._id || targetSubmissionId,
          currentStep: savedStep,
          rmcAccessConfigured: true,
          rmcPasswordConfigured: true,
        }, storageScope);
        setRmcPasswordConfigured(true);
      }

      toast.success('Formulario guardado. Ya podés volver con tu email y contraseña.');
      setSaveLaterDialogOpen(false);
      resolveSaveLaterRequest(true);
    } catch (error) {
      console.error('Save for later error:', error);
      const detail = error?.response?.data?.detail;
      if (error?.response?.status === 401 && (detail === 'password_required' || detail === 'password_invalid')) {
        setResumePasswordRequired(true);
        toast.error('Ese email ya tiene un formulario guardado. Usá la contraseña correcta para continuar.');
      } else {
        toast.error(detail || 'No se pudo guardar para continuar más tarde.');
      }
    } finally {
      setIsSavingForLaterAccess(false);
    }
  };

  const handleStepClick = (step) => {
    if (isSubmissionLocked) return;
    if (step <= currentStep) {
      saveToServer();
      setCurrentStep(step);
    }
  };

  const handleAnswerChange = (questionId, value) => {
    try {
      // Validate that the value is serializable before updating state
      if (value !== undefined && value !== null) {
        JSON.stringify(value); // Test serialization
      }
      setQuestionAnswers((prev) => {
        const next = { ...prev, [questionId]: value };
        return next;
      });
    } catch (err) {
      console.error('Error in handleAnswerChange for', questionId, err);
    }
  };

  // Get questions for each section
  // Step 1: Preguntas iniciales (A, B) - ANTES de Cuadraditos
  // Step 3: Preguntas contexto (C, D, E, F, G, H) - DESPUÉS de Cuadraditos y ANTES de Rueda
  // Step 5: Preguntas sobre Rueda (J) + preguntas dinámicas por áreas bajas - DESPUÉS de la Rueda
  // Step 7 (after objectives): Preguntas sobre Objetivos (K) y Cierre (L)
  const getQuestionsForStep = (stepNum) => {
    switch (stepNum) {
      case 1:
        // Preguntas iniciales + Contexto personal
        return [
          ...coachingQuestions.sectionA.questions,
          ...coachingQuestions.sectionB.questions
        ];
      case 3:
        // Dinero/Trabajo, Rutina, Sustancias, Organización, Negocio, IA (sin Respiración)
        return [
          ...coachingQuestions.sectionC.questions,
          ...coachingQuestions.sectionD.questions,
          ...coachingQuestions.sectionE.questions,
          ...coachingQuestions.sectionF.questions,
          ...coachingQuestions.sectionG.questions,
          ...coachingQuestions.sectionH.questions
        ];
      case 5:
        // Preguntas sobre la Rueda de la Vida + preguntas dinámicas por áreas con puntaje < 4
        const dynamicQuestions = generateLowScoreQuestions(wheelData);
        return [
          ...coachingQuestions.sectionJ.questions,
          ...dynamicQuestions
        ];
      case 'post-objectives':
        // Preguntas sobre Objetivos y Cierre (después de definir objetivos)
        return [
          ...coachingQuestions.sectionK.questions,
          ...coachingQuestions.sectionL.questions
        ];
      default:
        return [];
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="flex flex-col items-center gap-4">
          <Loader2 className="w-8 h-8 animate-spin text-primary" />
          <p className="text-muted-foreground">Cargando tu progreso...</p>
        </div>
      </div>
    );
  }

  if (portalHandoffError) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background p-4">
        <div className="max-w-lg rounded-xl border bg-card p-8 text-center shadow-sm">
          <AlertCircle className="w-12 h-12 text-amber-500 mx-auto" />
          <h1 className="mt-4 text-2xl font-bold">No pudimos abrir tu Planificador</h1>
          <p className="mt-3 text-muted-foreground">{portalHandoffError}</p>
          <Button asChild className="mt-6 w-full">
            <a href={PORTAL_URL}>Volver al Portal del Alumno</a>
          </Button>
        </div>
      </div>
    );
  }

  const renderStep = () => {
    switch (currentStep) {
      case 0:
        return (
          <StepUserData
            userData={userData}
            onUserDataChange={setUserData}
            capacitacionId={capacitacionId}
            onCapacitacionChange={setCapacitacionId}
            resumePasswordRequired={resumePasswordRequired}
            resumePassword={resumePassword}
            onResumePasswordChange={setResumePassword}
            lockedIdentity={portalIdentity}
            onNext={handleNext}
          />
        );

      case 1:
        return (
          <StepQuestions
            title="Preguntas Iniciales"
            description="Respondamos algunas preguntas para conocerte mejor."
            questions={getQuestionsForStep(1)}
            answers={questionAnswers}
            onAnswerChange={handleAnswerChange}
            onNext={handleNext}
            onBack={handleBack}
          />
        );

      case 2:
        return (
          <StepLifeSquares
            lifeSquares={lifeSquares}
            onLifeSquaresChange={setLifeSquares}
            userName={userData.name}
            onNext={handleNext}
            onBack={handleBack}
          />
        );

      case 3:
        return (
          <StepQuestions
            title="Contexto Personal y Profesional"
            description="Continuemos con preguntas sobre tu vida diaria, trabajo y hábitos."
            questions={getQuestionsForStep(3)}
            answers={questionAnswers}
            onAnswerChange={handleAnswerChange}
            onNext={handleNext}
            onBack={handleBack}
          />
        );

      case 4:
        return (
          <StepWheel
            wheelData={wheelData}
            onWheelDataChange={setWheelData}
            onNext={handleNext}
            onBack={handleBack}
          />
        );

      case 5:
        return (
          <StepQuestions
            title="Reflexión sobre tu Rueda de la Vida"
            description="Ahora que completaste tu Rueda, reflexionemos sobre los resultados."
            questions={getQuestionsForStep(5)}
            answers={questionAnswers}
            onAnswerChange={handleAnswerChange}
            onNext={handleNext}
            onBack={handleBack}
          />
        );

      case 6:
        return (
          <StepObjectives
            objectives={objectives}
            onObjectivesChange={setObjectives}
            onNext={handleNext}
            onBack={handleBack}
          />
        );

      case 7:
        return (
          <StepQuestions
            title="Compromisos y Cierre"
            description="Definamos acciones concretas y compromisos para avanzar."
            questions={getQuestionsForStep('post-objectives')}
            answers={questionAnswers}
            onAnswerChange={handleAnswerChange}
            onNext={handleNext}
            onBack={handleBack}
          />
        );

      case 8:
        if (useLegacyAudioSteps) {
          return (
            <StepAffirmationPhrases
              submissionId={submissionId}
              sessionId={sessionId}
              phrases={affirmationPhrases}
              onPhrasesChange={setAffirmationPhrases}
              onAudioTokenChange={setAudioToken}
              onAudioUrlChange={setAudioUrl}
              onNext={handleNext}
              onBack={handleBack}
            />
          );
        }

        return (
          <StepSummary
            userData={userData}
            wheelData={wheelData}
            lifeSquares={lifeSquares}
            objectives={objectives}
            questionAnswers={questionAnswers}
            sessionId={sessionId}
            submissionId={submissionId}
            isFormu2={false}
            portalJourney={isPortalJourney}
            portalUrl={PORTAL_URL}
            onBack={isSubmissionLocked ? undefined : handleBack}
          />
        );

      case 9:
        if (!useLegacyAudioSteps) return null;
        return (
          <StepAudioOptional
            submissionId={submissionId}
            sessionId={sessionId}
            phrases={affirmationPhrases}
            audioToken={audioToken}
            audioUrl={audioUrl}
            audioStatus={audioStatus}
            onAudioTokenChange={setAudioToken}
            onAudioUrlChange={setAudioUrl}
            onAudioStatusChange={setAudioStatus}
            onSaveForLater={requestSaveForLater}
            onNext={handleNext}
            onBack={handleBack}
          />
        );

      case 10:
        if (!useLegacyAudioSteps) return null;
        return (
          <StepSummary
            userData={userData}
            wheelData={wheelData}
            lifeSquares={lifeSquares}
            objectives={objectives}
            questionAnswers={questionAnswers}
            sessionId={sessionId}
            submissionId={submissionId}
            isFormu2
            audioStatus={audioStatus}
            rmcPasswordConfigured={rmcPasswordConfigured}
            onRmcPasswordConfigured={() => setRmcPasswordConfigured(true)}
            onAudioTokenChange={setAudioToken}
            onAudioUrlChange={setAudioUrl}
            onBack={isSubmissionLocked ? undefined : handleBack}
          />
        );

      default:
        return null;
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <Toaster richColors position="top-center" />
      <SolutgenSupportWidget
        enabled={Boolean(userData.email)}
        user={{
          id: submissionId || userData.email,
          name: userData.name,
          email: userData.email,
          role: 'student'
        }}
        context={{
          module: window.location.pathname,
          submissionId: submissionId || '',
          formVariant,
          currentStep
        }}
      />
      
      {/* Header */}
      <header className="sticky top-0 z-50 bg-background/80 backdrop-blur-sm border-b">
        <div className="max-w-screen-xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h1 className="text-xl font-bold text-primary">Academia Cortex</h1>
              <p className="text-xs text-muted-foreground">Planificador de Vida</p>
            </div>
            <div className="flex items-center gap-3">
              {userData.name && (
                <div className="text-right">
                  <p className="text-sm font-medium">{userData.name}</p>
                  <p className="text-xs text-muted-foreground">Guardado automáticamente</p>
                </div>
              )}
              <ThemeToggle />
            </div>
          </div>
          <WizardProgress currentStep={currentStep} onStepClick={handleStepClick} steps={steps} />
          {offerSaveForLaterPassword && !isSubmissionLocked && currentStep < summaryStep && (
            <div className="mt-3 flex justify-end">
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={requestSaveForLater}
                disabled={isSavingForLaterAccess}
                className="w-full sm:w-auto"
              >
                {isSavingForLaterAccess ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <Save className="w-4 h-4" />
                )}
                Guardar y continuar más tarde
              </Button>
            </div>
          )}
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-screen-xl mx-auto px-4 py-8">
        {renderStep()}
      </main>

      <Dialog
        open={saveLaterDialogOpen}
        onOpenChange={(open) => {
          if (open) {
            setSaveLaterDialogOpen(true);
          } else {
            closeSaveLaterDialog();
          }
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Guardar y continuar más tarde</DialogTitle>
            <DialogDescription>
              Tu usuario será el email {userData.email ? <strong>{userData.email}</strong> : 'que cargaste en Datos'}.
              Creá una contraseña para volver a este formulario desde cualquier dispositivo.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleConfirmSaveForLater} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="save-later-password">Contraseña</Label>
              <Input
                id="save-later-password"
                type="password"
                value={saveLaterPassword}
                onChange={(event) => setSaveLaterPassword(event.target.value)}
                autoComplete="new-password"
                minLength={8}
                disabled={isSavingForLaterAccess}
                required
              />
              <p className="text-xs text-muted-foreground">Mínimo 8 caracteres.</p>
            </div>
            <div className="space-y-2">
              <Label htmlFor="save-later-password-confirm">Confirmar contraseña</Label>
              <Input
                id="save-later-password-confirm"
                type="password"
                value={saveLaterPasswordConfirm}
                onChange={(event) => setSaveLaterPasswordConfirm(event.target.value)}
                autoComplete="new-password"
                minLength={8}
                disabled={isSavingForLaterAccess}
                required
              />
            </div>
            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={closeSaveLaterDialog}
                disabled={isSavingForLaterAccess}
              >
                Cancelar
              </Button>
              <Button type="submit" disabled={isSavingForLaterAccess}>
                {isSavingForLaterAccess ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
                Guardar acceso
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Footer */}
      <footer className="border-t py-6 mt-8">
        <div className="max-w-screen-xl mx-auto px-4 text-center">
          <p className="text-sm text-muted-foreground">
            © {new Date().getFullYear()} Academia Cortex - Planifica tu vida con claridad y propósito
          </p>
        </div>
      </footer>
    </div>
  );
}

// Main App component with routing
function App() {
  const [pathName, setPathName] = useState(window.location.pathname);
  const [portalJourneyRequest] = useState(() =>
    getPortalJourneyRequest(window.location)
  );

  useEffect(() => {
    const handlePopState = () => {
      setPathName(window.location.pathname);
    };

    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, []);

  useEffect(() => {
    let robots = document.querySelector('meta[name="robots"]');
    if (pathName.startsWith('/formu2') || pathName.startsWith('/audio') || pathName.startsWith('/classic')) {
      if (!robots) {
        robots = document.createElement('meta');
        robots.setAttribute('name', 'robots');
        document.head.appendChild(robots);
      }
      robots.setAttribute('content', 'noindex,nofollow');
    } else if (robots) {
      robots.remove();
    }
  }, [pathName]);

  if (isAdminRoute()) {
    return (
      <ErrorBoundary>
        <AdminApp />
      </ErrorBoundary>
    );
  }

  if (isAudioRoute()) {
    return (
      <ErrorBoundary>
        <AudioPage />
      </ErrorBoundary>
    );
  }

  return (
    <ErrorBoundary>
      <FormApp
        formVariant={getFormVariantFromPath()}
        portalJourneyRequest={portalJourneyRequest}
      />
    </ErrorBoundary>
  );
}

export default App;
